import { Link } from "@inertiajs/react";
import {
  Heading,
  Text,
  Grid,
  Card,
  Flex,
  Button,
} from "@radix-ui/themes";
import UserPageShell from "@/shared/users/UserPageShell";
import EventCard from "@/shared/events/EventCard";

interface CalendarEvent {
  id: number;
  name: string;
  date: string;
  time_until: string;
  is_past: boolean;
  track_name: string;
  track_slug: string;
  poster_url: string | null;
  club_name: string | null;
  club_slug: string | null;
  reasons: string[];
}

interface WatchedTrack {
  id: number;
  name: string;
  city: string;
  state: string;
  slug: string;
}

interface WatchedClub {
  id: number;
  name: string;
  locale: string | null;
  slug: string;
  image_url: string | null;
}

interface CalendarProps {
  upcoming_events: CalendarEvent[];
  recent_events: CalendarEvent[];
  watched_tracks: WatchedTrack[];
  watched_clubs: WatchedClub[];
}

function EmptyState({ message, linkHref, linkLabel }: { message: string; linkHref?: string; linkLabel?: string }) {
  return (
    <Flex direction="column" align="center" gap="3" py="6">
      <Text color="gray" size="2">{message}</Text>
      {linkHref && linkLabel && (
        <Link href={linkHref}>
          <Button variant="soft">{linkLabel}</Button>
        </Link>
      )}
    </Flex>
  );
}

export default function Calendar({
  upcoming_events,
  recent_events,
  watched_tracks,
  watched_clubs,
}: CalendarProps) {
  return (
    <UserPageShell title="My Calendar">
      <section>
        <Heading mb="4">Upcoming</Heading>
        {upcoming_events.length > 0 ? (
          <Grid columns={{ initial: "1", sm: "2", md: "4" }} gap="4">
            {upcoming_events.map((event) => (
              <EventCard key={event.id} {...event} />
            ))}
          </Grid>
        ) : (
          <EmptyState
            message="Nothing coming up yet. Find some events, tracks, or clubs to follow!"
            linkHref="/events"
            linkLabel="Browse Events"
          />
        )}
      </section>

      <section>
        <Heading mb="4">Recent</Heading>
        {recent_events.length > 0 ? (
          <Grid columns={{ initial: "1", sm: "2", md: "4" }} gap="4">
            {recent_events.map((event) => (
              <EventCard key={event.id} {...event} />
            ))}
          </Grid>
        ) : (
          <EmptyState message="No events in the past month." />
        )}
      </section>

      <section>
        <Heading mb="4">Watch List</Heading>

        {watched_tracks.length === 0 && watched_clubs.length === 0 ? (
          <EmptyState
            message="You're not watching any tracks or clubs yet."
            linkHref="/tracks"
            linkLabel="Browse Tracks"
          />
        ) : (
          <>
            {watched_tracks.length > 0 && (
              <>
                <Text size="3" weight="bold" as="div" mb="3">Tracks</Text>
                <Grid columns={{ initial: "1", sm: "2", md: "4" }} gap="4" mb="6">
                  {watched_tracks.map((track) => (
                    <Link key={track.id} href={`/tracks/${track.slug}`} style={{ textDecoration: "none", color: "inherit" }}>
                      <Card size="2" style={{ cursor: "pointer", height: "100%" }}>
                        <Text as="div" size="3" weight="bold" mb="1">{track.name}</Text>
                        <Text as="div" size="2" color="gray">
                          {track.city}, {track.state}
                        </Text>
                      </Card>
                    </Link>
                  ))}
                </Grid>
              </>
            )}

            {watched_clubs.length > 0 && (
              <>
                <Text size="3" weight="bold" as="div" mb="3">Clubs</Text>
                <Grid columns={{ initial: "1", sm: "2", md: "4" }} gap="4">
                  {watched_clubs.map((club) => (
                    <Link key={club.id} href={`/clubs/${club.slug}`} style={{ textDecoration: "none", color: "inherit" }}>
                      <Card size="2" style={{ cursor: "pointer", height: "100%" }}>
                        {club.image_url && (
                          <img
                            src={club.image_url}
                            alt={club.name}
                            style={{
                              width: "100%",
                              height: 80,
                              objectFit: "cover",
                              borderRadius: "var(--radius-2)",
                              marginBottom: "0.5rem",
                            }}
                          />
                        )}
                        <Text as="div" size="3" weight="bold" mb="1">{club.name}</Text>
                        {club.locale && (
                          <Text as="div" size="2" color="gray">{club.locale}</Text>
                        )}
                      </Card>
                    </Link>
                  ))}
                </Grid>
              </>
            )}
          </>
        )}
      </section>
    </UserPageShell>
  );
}
